import { getAddress, zeroPadValue } from 'ethers'

import { getLibertyApiEnv, LibertyApiEnv } from './apiBase'

/**
 * Integrator ids are bytes32 (a left-padded EVM address) and identify which
 * front-end originated a swap. The new LibertySwap contracts read it from the
 * V2 `swap()` call, the CCTP hook payload and both gasless permit flows.
 * Known integrators: Liberty Swap -> zero, gopulse -> 0x8ecF...c92A, ZKX -> below.
 *
 * Per environment, like the gasless relayer contract: liberty-swap ships this as
 * `NEXT_PUBLIC_CCTP_DEFAULT_INTEGRATOR_{DEV,PROD}`, and the two backends have
 * separate integrator registries. The id is baked into signed payloads (the
 * EIP-3009 nonce preimage and the EIP-2612 SwapParams), so a value from the
 * wrong environment yields a valid signature the relayer can only reject —
 * after the user has already signed.
 *
 * `dev` is the address every dev-backend test has been run against; `prod` is
 * the production value supplied by the LibertySwap team on 2026-08-19.
 */
export const ZKX_INTEGRATOR_ADDRESSES: Record<LibertyApiEnv, string> = {
  dev: '0x7baC3688b4D0560b2f31AebAC41A5cfa7a3E185a',
  // uat mirrors prod — same backend, same integrator registry.
  uat: '0xc8838a64a68b79793ADE02ff3A7E33851988C5C8',
  prod: '0xc8838a64a68b79793ADE02ff3A7E33851988C5C8'
}

let integratorAddressOverride: string | null = null

/**
 * Override the integrator address for the current build
 * (`NEXT_PUBLIC_CCTP_DEFAULT_INTEGRATOR`). This module is env-agnostic
 * (submodule rule), so the app reads the value and pushes it in at boot — same
 * shape as `setGaslessV2Contract`. Pass null/empty to fall back to the table.
 *
 * It exists because the address has to be registered in LibertySwap's on-chain
 * IntegratorRegistry: when they rotate or re-register one, `/swap/quote` answers
 * `400 Integrator <addr> is not registered in <registry>` for every quote, and
 * waiting on a submodule bump to unbreak swapping is too slow.
 */
export function setZkxIntegratorAddress(address: string | null | undefined): void {
  const trimmed = typeof address === 'string' ? address.trim() : ''
  integratorAddressOverride = trimmed || null
}

/** The ZKX integrator address for the environment the stack is pointed at. */
export const getZkxIntegratorAddress = (): string =>
  integratorAddressOverride || ZKX_INTEGRATOR_ADDRESSES[getLibertyApiEnv()]

/** The Liberty Swap integrator, also used for fee-burned tokens. */
export const ZERO_INTEGRATOR_ID = `0x${'0'.repeat(64)}`

/**
 * Fee-burned tokens: their fee is burned rather than split, so the routers take
 * no integrator fee and reject any integrator but the zero one. PCOCK has always
 * been one; pDAI joined the list when liberty migrated it to the V3 routers,
 * which revert `IntegratorNotSupported()` (0x1261f5d4) on both chain 1
 * (0x4DAc8619…) and 369 (0xF5466fC7…) for the ZKX integrator — confirmed by the
 * LibertySwap team on 2026-09-03.
 */
export const BURNED_TOKEN_IDS = [
  'PCOCK-ETHEREUM',
  'PCOCK-BSC',
  'PCOCK-PULSECHAIN',
  'PDAI-ETHEREUM',
  'PDAI-PULSECHAIN'
]

const toBytes32IntegratorId = (value: string): string => {
  if (/^0x[a-fA-F0-9]{64}$/.test(value)) return value.toLowerCase()

  return zeroPadValue(getAddress(value), 32).toLowerCase()
}

/**
 * The integrator id to send with a swap of `inputTokenId`. The value is baked
 * into signed payloads (the EIP-3009 nonce preimage and the EIP-2612
 * SwapParams), so it must match what the relayer re-derives.
 */
export function getDefaultIntegratorId(inputTokenId?: string): string {
  if (inputTokenId && BURNED_TOKEN_IDS.includes(inputTokenId)) return ZERO_INTEGRATOR_ID

  return toBytes32IntegratorId(getZkxIntegratorAddress())
}
